import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  EyeOff,
  HelpCircle,
  Loader2,
  PlusCircle,
  RefreshCw,
  Search,
  Undo2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Checkbox } from "@/components/ui/Checkbox";
import { FormField } from "@/components/ui/FormField";
import { Modal } from "@/components/ui/Modal";
import { useRole } from "@/contexts/RoleContext";
import { BienDetailModal } from "@/v2/components/BienDetailModal";
import {
  SYNC_CATEGORIES,
  applySyncItem,
  associateSyncItem,
  countByCategory,
  decideSyncItem,
  fetchBienSummaries,
  fetchLatestSyncRun,
  fetchSyncItems,
  hasWork,
  immowebUrl,
  isPhotosOnlySame,
  planFor,
  processSyncRun,
  revertSyncItem,
  searchBiens,
  startSyncRun,
  type BienSummary,
  type SyncCategory,
  type SyncDecision,
  type SyncItem,
} from "@/lib/immowebSync";
import { fieldLabel } from "@/lib/immowebSyncLabels";

/*
 * Synchronisation Immoweb (C-Level). Principe validé par Thibaud le 2026-10-19 :
 * Immoweb est la dernière version du bien. Trois issues par annonce :
 *   - « Mise à jour » : bien retrouvé (code, référence, adresse) → Immoweb remplace ;
 *   - « Création » : bien introuvable → nouvelle fiche ;
 *   - « À vérifier » : plusieurs biens possibles → on choisit.
 * Plus la liste des biens « Disponible » qui ne sont plus en ligne.
 */

const syncKeys = {
  latestRun: ["immoweb-sync", "latest-run"] as const,
  items: (runId: string) => ["immoweb-sync", "items", runId] as const,
  biens: (ids: string) => ["immoweb-sync", "biens", ids] as const,
};

type TabKey = SyncCategory | "error";

const CATEGORY_ICON: Record<TabKey, typeof RefreshCw> = {
  update: RefreshCw,
  create: PlusCircle,
  review: HelpCircle,
  vanished: EyeOff,
  error: HelpCircle,
};

function formatValue(t: TFunction, v: unknown): string {
  if (v === null || v === undefined || v === "") return t("v2.immowebSync.values.empty");
  if (typeof v === "boolean") return v ? t("v2.immowebSync.values.yes") : t("v2.immowebSync.values.no");
  if (Array.isArray(v)) {
    if (!v.length) return t("v2.immowebSync.values.empty");
    return v
      .map((x) => (x && typeof x === "object" && "surface" in x ? `${(x as { surface: string }).surface} m²` : String(x)))
      .join(", ");
  }
  const s = String(v);
  return s.length > 140 ? `${s.slice(0, 140)}…` : s;
}

/** Résumé en langage clair de ce que l'application fera pour une annonce. */
function planSentence(t: TFunction, item: SyncItem): string {
  const p = planFor(item);
  if (p.skip) return t("v2.immowebSync.summary.skipped");
  const parts: string[] = [];
  if (p.create) parts.push(t("v2.immowebSync.summary.create"));
  const changed = p.fills + p.overwrites;
  if (changed) parts.push(t("v2.immowebSync.summary.changed", { count: changed }));
  if (p.photos) parts.push(t("v2.immowebSync.summary.photos"));
  if (p.status) parts.push(t("v2.immowebSync.summary.status", { status: p.status }));
  return parts.length ? parts.join(" · ") : t("v2.immowebSync.summary.upToDate");
}

function appliedSentence(t: TFunction, item: SyncItem): string {
  const r = item.apply_result ?? {};
  const parts: string[] = [];
  if (r.created) parts.push(t("v2.immowebSync.summary.created"));
  const changed = Object.keys(r.after ?? {}).length;
  if (!r.created && changed) parts.push(t("v2.immowebSync.summary.changed", { count: changed }));
  if (r.photos) parts.push(t("v2.immowebSync.summary.photosDone", { count: r.photos }));
  return parts.length ? parts.join(" · ") : t("v2.immowebSync.summary.nothing");
}

/** Libellé d'un bien : sa référence interne, sinon « sans référence » (jamais l'identifiant technique). */
const bienLabel = (t: TFunction, b: { referenceInterne: string | null }) =>
  b.referenceInterne?.trim() || t("v2.immowebSync.bien.noRef");

// ─── Page ──────────────────────────────────────────────────────────────────

export function ImmowebSyncPage() {
  const { t, i18n } = useTranslation();
  const { role } = useRole();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>("update");
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [modalBienId, setModalBienId] = useState<string | null>(null);
  // Annonces visées par le récapitulatif : toutes (bouton global) ou une seule.
  const [recapItems, setRecapItems] = useState<SyncItem[] | null>(null);
  const [applying, setApplying] = useState<{ done: number; total: number; errors: string[]; finished: boolean } | null>(null);

  const { data: run, isLoading: runLoading } = useQuery({
    queryKey: syncKeys.latestRun,
    queryFn: fetchLatestSyncRun,
    enabled: role === "c_level",
  });
  const { data: items = [] } = useQuery({
    queryKey: run ? syncKeys.items(run.id) : ["immoweb-sync", "items", "none"],
    queryFn: () => (run ? fetchSyncItems(run.id) : Promise.resolve([])),
    enabled: !!run,
    refetchInterval: running ? 3000 : false,
  });
  const bienIds = useMemo(
    () => [...new Set(items.map((i) => i.bien_id).filter((x): x is string => !!x))].sort(),
    [items],
  );
  const { data: biens = {} } = useQuery({
    queryKey: syncKeys.biens(bienIds.join(",")),
    queryFn: () => fetchBienSummaries(bienIds),
    enabled: bienIds.length > 0,
  });

  const counts = useMemo(() => countByCategory(items), [items]);
  const total = items.filter((i) => i.code_immoweb).length;
  const analysed = items.filter((i) => i.code_immoweb && i.status !== "pending").length;
  const canResume = !running && run?.status === "analysing" && counts.pending > 0;
  // « Tout appliquer » : mises à jour et créations. Les « disparues » se
  // traitent une par une (choix Loué / Vendu), « à vérifier » attend un choix.
  // Même annonce déjà importée (seules les photos « changent », même nombre).
  const photosOnly = useMemo(() => items.filter((i) => isPhotosOnlySame(i) && !i.applied_at && !i.decision?.skip), [items]);
  const [skipping, setSkipping] = useState(false);

  /** Met de côté en un clic toutes les annonces « photos seulement, même nombre ». */
  async function skipPhotosOnly() {
    setSkipping(true);
    try {
      for (const it of photosOnly) {
        const next = { ...(it.decision ?? {}), skip: true };
        qc.setQueryData<SyncItem[]>(syncKeys.items(run!.id), (list) =>
          (list ?? []).map((i) => (i.id === it.id ? { ...i, decision: next } : i)),
        );
        await decideSyncItem(it.id, next);
      }
    } catch (e) {
      setRunError(e instanceof Error ? e.message : String(e));
    } finally {
      setSkipping(false);
      await qc.invalidateQueries({ queryKey: ["immoweb-sync"] });
    }
  }

  const toApply = useMemo(
    () => items.filter((i) => (i.category === "update" || i.category === "create") && hasWork(i)),
    [items],
  );

  async function drain(runId: string) {
    for (let guard = 0; guard < 100; guard++) {
      const res = await processSyncRun(runId);
      await qc.invalidateQueries({ queryKey: syncKeys.items(runId) });
      if (res.done) break;
    }
  }

  async function launch(resumeRunId?: string) {
    setRunError(null);
    setRunning(true);
    try {
      let runId = resumeRunId;
      if (!runId) {
        const started = await startSyncRun();
        runId = started.run_id;
        await qc.invalidateQueries({ queryKey: syncKeys.latestRun });
      }
      await drain(runId);
    } catch (e) {
      setRunError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
      await qc.invalidateQueries({ queryKey: ["immoweb-sync"] });
    }
  }

  /** Applique les annonces une par une (photos comprises : un appel par annonce). */
  async function applyAll() {
    const list = recapItems ?? [];
    setApplying({ done: 0, total: list.length, errors: [], finished: false });
    // Les choix sont enregistrés 400 ms après le clic : on laisse passer le dernier.
    await new Promise((r) => setTimeout(r, 800));
    const errors: string[] = [];
    for (let i = 0; i < list.length; i++) {
      try {
        const res = await applySyncItem(list[i].id);
        if (!res.ok) errors.push(`${list[i].prefix ?? list[i].code_immoweb} : ${res.result?.error ?? "?"}`);
      } catch (e) {
        errors.push(`${list[i].prefix ?? list[i].code_immoweb} : ${e instanceof Error ? e.message : String(e)}`);
      }
      setApplying({ done: i + 1, total: list.length, errors: [...errors], finished: false });
    }
    setApplying({ done: list.length, total: list.length, errors, finished: true });
    await qc.invalidateQueries({ queryKey: ["immoweb-sync"] });
    await qc.invalidateQueries({ queryKey: ["biens"] });
  }

  if (role !== "c_level") {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-surface border border-border rounded-lg p-6 text-sm text-text-secondary">
          {t("v2.immowebSync.reservedCLevel")}
        </div>
      </div>
    );
  }

  const tabs: { key: TabKey; count: number }[] = [
    ...SYNC_CATEGORIES.map((c) => ({ key: c as TabKey, count: counts[c] })),
    ...(counts.error ? [{ key: "error" as TabKey, count: counts.error }] : []),
  ];
  const visible = items.filter((i) => (tab === "error" ? i.status === "error" : i.status === "analysed" && i.category === tab));
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  // Analyse faite avec l'ancien découpage (9 catégories) : il faut la relancer.
  const outdated = items.some((i) => i.category && !SYNC_CATEGORIES.includes(i.category));

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <RefreshCw size={22} className="text-accent" />
          {t("v2.immowebSync.title")}
        </h1>
        <p className="text-sm text-text-muted mt-1">{t("v2.immowebSync.subtitle")}</p>
      </div>

      <div className="bg-surface border border-border rounded-lg shadow-sm p-4 mb-5 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0 text-sm">
          {runLoading ? (
            <span className="text-text-muted">…</span>
          ) : run ? (
            <>
              <div className="text-text-primary font-medium">
                {t("v2.immowebSync.lastRun", { date: dateFmt.format(new Date(run.created_at)) })}
                <span className="ml-2 text-text-muted font-normal">{t(`v2.immowebSync.status.${run.status}`)}</span>
              </div>
              <div className="text-text-muted mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {(["location", "vente"] as const).map((tx) =>
                  run.stats?.[tx] ? (
                    <span key={tx}>
                      {t(`v2.immowebSync.tx.${tx}`)} :{" "}
                      {t("v2.immowebSync.readOfAnnounced", { read: run.stats[tx]!.read, announced: run.stats[tx]!.announced })}
                    </span>
                  ) : null,
                )}
              </div>
              {run.error && <div className="text-danger mt-1">{run.error}</div>}
            </>
          ) : (
            <span className="text-text-muted">{t("v2.immowebSync.never")}</span>
          )}
          {(running || canResume) && total > 0 && (
            <div className="mt-3">
              <div className="h-1.5 bg-gray-200 rounded overflow-hidden">
                <div className="h-full bg-accent transition-all" style={{ width: `${Math.round((analysed / total) * 100)}%` }} />
              </div>
              <div className="text-xs text-text-muted mt-1">{t("v2.immowebSync.progress", { done: analysed, total })}</div>
            </div>
          )}
          {runError && <div className="text-danger mt-2">{runError}</div>}
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {canResume && (
            <Button variant="secondary" onClick={() => launch(run!.id)}>
              {t("v2.immowebSync.resume")}
            </Button>
          )}
          <Button
            variant={run && !outdated ? "secondary" : "primary"}
            onClick={() => launch()}
            disabled={running || (!!applying && !applying.finished)}
          >
            {running ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {running ? t("v2.immowebSync.running") : t("v2.immowebSync.launch")}
          </Button>
          {run?.status === "ready" && !outdated && (
            <Button onClick={() => { setApplying(null); setRecapItems(toApply); }} disabled={running || !toApply.length}>
              <CheckCircle2 size={16} />
              {t("v2.immowebSync.apply.button", { count: toApply.length })}
            </Button>
          )}
        </div>
      </div>

      {run && outdated && (
        <div className="bg-surface border border-border rounded-lg p-4 mb-5 text-sm text-text-secondary">
          {t("v2.immowebSync.outdated")}
        </div>
      )}

      {run && !outdated && (
        <>
          <p className="text-xs text-text-muted mb-3">{t("v2.immowebSync.readOnlyNote")}</p>

          <div className="flex gap-1 mb-2 border-b border-border overflow-x-auto">
            {tabs.map(({ key, count }) => {
              const Icon = CATEGORY_ICON[key];
              const active = tab === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={cn(
                    "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap outline-none",
                    active ? "border-accent text-text-primary" : "border-transparent text-text-muted hover:text-text-primary",
                  )}
                >
                  <Icon size={14} className={active ? "text-accent" : "text-text-muted"} />
                  <span>{t(`v2.immowebSync.categories.${key}`)}</span>
                  <span className={cn("text-xs px-1.5 rounded", active ? "bg-accent-light text-accent" : "bg-gray-100 text-text-muted")}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
            <p className="flex-1 text-sm text-text-muted">{t(`v2.immowebSync.categoryHelp.${tab}`)}</p>
            {tab === "update" && photosOnly.length > 0 && (
              <Button size="sm" variant="secondary" disabled={skipping || running} onClick={skipPhotosOnly}>
                {skipping ? <Loader2 size={14} className="animate-spin" /> : <EyeOff size={14} />}
                {t("v2.immowebSync.skipPhotosOnly", { count: photosOnly.length })}
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {visible.length === 0 ? (
              <div className="text-sm text-text-muted bg-surface border border-border rounded-lg p-6 text-center">
                {t("v2.immowebSync.emptyTab")}
              </div>
            ) : (
              visible.map((item) => (
                <SyncItemRow
                  key={item.id}
                  item={item}
                  runId={run.id}
                  bien={item.bien_id ? biens[item.bien_id] : undefined}
                  onOpenBien={setModalBienId}
                  onApplyOne={(it) => { setApplying(null); setRecapItems([it]); }}
                />
              ))
            )}
          </div>
        </>
      )}

      <ApplyModal
        open={!!recapItems}
        items={recapItems ?? []}
        applying={applying}
        onCancel={() => setRecapItems(null)}
        onConfirm={applyAll}
      />
      <BienDetailModal bienId={modalBienId} onClose={() => setModalBienId(null)} />
    </div>
  );
}

// ─── Récapitulatif avant application ───────────────────────────────────────

function ApplyModal({
  open,
  items,
  applying,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  items: SyncItem[];
  applying: { done: number; total: number; errors: string[]; finished: boolean } | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const plans = items.map(planFor);
  const n = (f: (p: ReturnType<typeof planFor>) => boolean) => plans.filter(f).length;
  const lines = [
    { key: "updated", count: n((p) => !p.create && (p.fills > 0 || p.overwrites > 0 || p.photos)) },
    { key: "created", count: n((p) => p.create) },
    { key: "photos", count: n((p) => p.photos) },
    { key: "status", count: n((p) => !!p.status) },
  ];
  const busy = !!applying && !applying.finished;

  return (
    <Modal isOpen={open} onClose={busy ? () => undefined : onCancel} title={t("v2.immowebSync.apply.title")}>
      <div className="flex flex-col gap-4 text-sm">
        {items.length === 0 ? (
          <p className="text-text-secondary">{t("v2.immowebSync.apply.nothing")}</p>
        ) : (
          <>
            <p className="text-text-secondary">{t("v2.immowebSync.apply.intro", { count: items.length })}</p>
            <ul className="flex flex-col gap-1">
              {lines.filter((l) => l.count > 0).map((l) => (
                <li key={l.key} className="flex justify-between border-b border-border last:border-0 py-1">
                  <span className="text-text-secondary">{t(`v2.immowebSync.apply.${l.key}`)}</span>
                  <span className="font-medium text-text-primary">{l.count}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-text-muted">{t("v2.immowebSync.apply.undoNote")}</p>
          </>
        )}

        {applying && (
          <div>
            <div className="h-1.5 bg-gray-200 rounded overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${applying.total ? Math.round((applying.done / applying.total) * 100) : 100}%` }} />
            </div>
            <div className="text-xs text-text-muted mt-1">
              {applying.finished
                ? t("v2.immowebSync.apply.done", { ok: applying.total - applying.errors.length, errors: applying.errors.length })
                : t("v2.immowebSync.apply.progress", { done: applying.done, total: applying.total })}
            </div>
            {applying.errors.length > 0 && (
              <ul className="mt-2 text-xs text-danger list-disc pl-5">
                {applying.errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="cancel" onClick={onCancel} disabled={busy}>
            {applying?.finished ? t("v2.immowebSync.apply.close") : t("v2.immowebSync.apply.cancel")}
          </Button>
          {!applying && items.length > 0 && (
            <Button onClick={onConfirm}>{t("v2.immowebSync.apply.confirm")}</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Carte d'annonce ────────────────────────────────────────────────────────

function SyncItemRow({
  item,
  runId,
  bien,
  onOpenBien,
  onApplyOne,
}: {
  item: SyncItem;
  runId: string;
  bien: BienSummary | undefined;
  onOpenBien: (id: string) => void;
  onApplyOne: (item: SyncItem) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const decision: SyncDecision = item.decision ?? {};
  const applied = !!item.applied_at;
  const reverted = !!item.apply_result?.reverted_at;
  const changes = item.diff.length;
  const canRevert = applied && !item.apply_result?.created && !!item.apply_result?.before && Object.keys(item.apply_result.before).length > 0;

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  function updateDecision(next: SyncDecision) {
    qc.setQueryData<SyncItem[]>(syncKeys.items(runId), (list) =>
      (list ?? []).map((i) => (i.id === item.id ? { ...i, decision: next } : i)),
    );
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      decideSyncItem(item.id, next).catch((e) => setError(e instanceof Error ? e.message : String(e)));
    }, 400);
  }

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setPicking(false);
      await qc.invalidateQueries({ queryKey: ["immoweb-sync"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const header = (
    <button
      type="button"
      onClick={() => setOpen((o) => !o)}
      className="w-full text-left p-4 flex items-start gap-3 cursor-pointer outline-none"
    >
      {open ? <ChevronDown size={16} className="mt-0.5 text-text-muted shrink-0" /> : <ChevronRight size={16} className="mt-0.5 text-text-muted shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={item.transaction}>{t(`v2.immowebSync.tx.${item.transaction}`)}</Badge>
          {item.prefix && <span className="text-sm font-semibold text-text-primary">{item.prefix}</span>}
          {item.code_immoweb && <span className="text-xs text-text-muted">#{item.code_immoweb}</span>}
          {item.category === "update" && bien && (
            <span className="text-xs text-text-muted">→ {bienLabel(t, bien)}</span>
          )}
        </div>
        <div className="text-sm text-text-secondary mt-1 truncate">{item.title_fr}</div>
        <div className="text-xs text-text-muted mt-0.5 truncate">{item.address}</div>
      </div>
      <div className="flex flex-wrap gap-1.5 justify-end shrink-0 max-w-[45%]">
        {applied && <Badge variant="success">{t("v2.immowebSync.applied")}</Badge>}
        {reverted && !applied && <Badge>{t("v2.immowebSync.revertedBadge")}</Badge>}
        {!applied && decision.skip && <Badge>{t("v2.immowebSync.skippedBadge")}</Badge>}
        {item.apply_result?.error && <Badge variant="danger">{t("v2.immowebSync.applyFailed")}</Badge>}
        {item.category === "update" && !applied && (
          <Badge variant={changes ? "accent" : undefined}>
            {changes ? t("v2.immowebSync.chips.changes", { count: changes }) : t("v2.immowebSync.chips.upToDate")}
          </Badge>
        )}
      </div>
    </button>
  );

  return (
    <div className={cn("bg-surface border border-border rounded-lg shadow-sm", decision.skip && "opacity-60")}>
      {header}

      {open && (
        <div className="border-t border-border p-4 flex flex-col gap-5">
          <div className="flex flex-wrap gap-4 text-sm">
            {item.code_immoweb && (
              <a href={immowebUrl(item.code_immoweb, item.transaction)} target="_blank" rel="noreferrer" className="text-accent inline-flex items-center gap-1">
                <ExternalLink size={14} /> {t("v2.immowebSync.openImmoweb")}
              </a>
            )}
            {item.photos.length > 0 && (
              <span className="text-text-muted">{t("v2.immowebSync.photosCount", { count: item.photos.length })}</span>
            )}
          </div>

          {item.error && <div className="text-sm text-danger">{item.error}</div>}
          {item.apply_result?.error && <div className="text-sm text-danger">{t("v2.immowebSync.applyError", { error: item.apply_result.error })}</div>}
          {item.apply_result?.photoError && <div className="text-sm text-danger">{t("v2.immowebSync.photoError", { error: item.apply_result.photoError })}</div>}

          {/* Disparues : un choix simple */}
          {item.category === "vanished" ? (
            <section>
              <p className="text-sm text-text-secondary mb-3">
                {t("v2.immowebSync.vanishedDetail", { code: (item.scraped?.codeImmoweb as string) ?? "" })}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {item.bien_id && (
                  <button type="button" onClick={() => onOpenBien(item.bien_id!)} className="text-accent text-sm cursor-pointer outline-none">
                    {t("v2.immowebSync.bien.open")}
                  </button>
                )}
                <Segmented
                  value={decision.vanished ?? "keep"}
                  disabled={applied || busy}
                  options={[
                    { value: "keep", label: t("v2.immowebSync.vanishedChoice.keep") },
                    { value: "LOUÉ", label: t("v2.immowebSync.vanishedChoice.loue") },
                    { value: "VENDU", label: t("v2.immowebSync.vanishedChoice.vendu") },
                  ]}
                  onChange={(v) => updateDecision({ ...decision, vanished: v as SyncDecision["vanished"] })}
                />
              </div>
            </section>
          ) : (
            <section>
              <div className="text-xs font-medium uppercase text-text-muted mb-2">{t("v2.immowebSync.bien.title")}</div>
              {item.category === "review" || picking ? (
                <BienPicker
                  item={item}
                  busy={busy}
                  onOpenBien={onOpenBien}
                  onPick={(id) => run(() => associateSyncItem(item.id, id))}
                  onClose={picking ? () => setPicking(false) : undefined}
                />
              ) : (
                <div className="flex flex-wrap items-center gap-3 text-sm border border-border rounded-md px-3 py-2">
                  {item.category === "update" && bien ? (
                    <>
                      <span className="font-medium text-text-primary">{bienLabel(t, bien)}</span>
                      <span className="text-text-secondary">{bien.adresse}</span>
                      <span className="text-xs text-text-muted">{bien.disponibilite}</span>
                      {item.match_method && <Badge>{t(`v2.immowebSync.matchMethod.${item.match_method}`)}</Badge>}
                      <button type="button" onClick={() => onOpenBien(bien.id)} className="text-accent text-sm cursor-pointer outline-none">
                        {t("v2.immowebSync.bien.open")}
                      </button>
                    </>
                  ) : (
                    <span className="text-text-secondary">
                      {item.bien_id && bien
                        ? t("v2.immowebSync.bien.createFromOther", { ref: bienLabel(t, bien) })
                        : t("v2.immowebSync.bien.create")}
                    </span>
                  )}
                  {!applied && (
                    <div className="ml-auto flex gap-2">
                      {item.category === "update" && (
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => associateSyncItem(item.id, null))}>
                          {t("v2.immowebSync.bien.createInstead")}
                        </Button>
                      )}
                      <Button size="sm" variant="secondary" disabled={busy} onClick={() => setPicking(true)}>
                        {item.category === "update" ? t("v2.immowebSync.bien.change") : t("v2.immowebSync.bien.pickExisting")}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Nouvelle fiche : référence et projet modifiables */}
          {item.category === "create" && !picking && !applied && (
            <section className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
              <FormField
                label={t("v2.immowebSync.newBien.ref")}
                value={decision.newRef ?? (bien?.referenceInterne || item.prefix || "")}
                onChange={(v) => updateDecision({ ...decision, newRef: v })}
              />
              <FormField
                label={t("v2.immowebSync.newBien.projet")}
                value={decision.newProjet ?? ""}
                onChange={(v) => updateDecision({ ...decision, newProjet: v })}
              />
            </section>
          )}

          {/* Ce qui change (lecture seule : Immoweb fait foi) */}
          {item.category === "update" && changes > 0 && !picking && (
            <section>
              <button
                type="button"
                onClick={() => setShowChanges((s) => !s)}
                className="text-sm text-accent cursor-pointer outline-none inline-flex items-center gap-1"
              >
                {showChanges ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {t("v2.immowebSync.showChanges", { count: changes })}
              </button>
              {showChanges && <ChangesTable item={item} />}
            </section>
          )}

          {item.unknown_fields.length > 0 && (
            <section>
              <div className="text-xs font-medium uppercase text-text-muted mb-2">{t("v2.immowebSync.unknownTitle")}</div>
              <ul className="text-sm text-text-secondary list-disc pl-5">
                {item.unknown_fields.map((u) => (
                  <li key={u.path}>
                    <code className="text-xs">{u.path}</code> = <strong>{String(u.value)}</strong>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {item.category !== "review" && !picking && (
            <section className="flex flex-col md:flex-row md:items-center gap-3 border-t border-border pt-4">
              <div className="flex-1 text-sm">
                <span className="text-text-muted">{applied ? t("v2.immowebSync.summary.done") : t("v2.immowebSync.summary.title")}</span>{" "}
                <span className="text-text-primary font-medium">{applied ? appliedSentence(t, item) : planSentence(t, item)}</span>
              </div>
              {!applied && item.category !== "vanished" && (
                <Checkbox
                  checked={!!decision.skip}
                  onChange={(c) => updateDecision({ ...decision, skip: c })}
                  label={t("v2.immowebSync.skip")}
                  readOnly={busy}
                />
              )}
              {!applied && hasWork(item) && (
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => onApplyOne(item)}>
                  {t("v2.immowebSync.applyOne")}
                </Button>
              )}
              {canRevert && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => run(() => revertSyncItem(item.id))}>
                  <Undo2 size={14} /> {t("v2.immowebSync.revert")}
                </Button>
              )}
            </section>
          )}

          {error && <div className="text-sm text-danger">{error}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Sélecteur de bien ─────────────────────────────────────────────────────

function BienPicker({
  item,
  busy,
  onOpenBien,
  onPick,
  onClose,
}: {
  item: SyncItem;
  busy: boolean;
  onOpenBien: (id: string) => void;
  onPick: (bienId: string | null) => void;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState<BienSummary[] | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) return;
    const h = setTimeout(() => {
      setSearching(true);
      searchBiens(term).then(setResults).catch(() => setResults([])).finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(h);
  }, [q]);

  // Moins de 2 caractères : on revient aux biens proposés par l'analyse.
  const shown = q.trim().length >= 2 ? results : null;
  const rows: { id: string; referenceInterne: string | null; adresse: string | null; transaction: string; disponibilite: string }[] =
    shown ?? item.candidates;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-text-secondary">{t("v2.immowebSync.picker.intro")}</p>
      <div className="relative">
        <FormField label={t("v2.immowebSync.picker.searchLabel")} value={q} onChange={setQ} />
        <Search size={14} className="absolute right-3 bottom-3 text-text-muted pointer-events-none" />
      </div>
      <div className="text-xs font-medium uppercase text-text-muted">
        {shown ? t("v2.immowebSync.picker.results") : t("v2.immowebSync.picker.candidates")}
        {searching && <Loader2 size={12} className="inline ml-2 animate-spin" />}
      </div>
      <div className="flex flex-col gap-2">
        {rows.length === 0 && <p className="text-sm text-text-muted">{t("v2.immowebSync.picker.noResult")}</p>}
        {rows.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-3 text-sm border border-border rounded-md px-3 py-2">
            <span className="font-medium text-text-primary">{bienLabel(t, c)}</span>
            <span className="text-text-secondary">{c.adresse}</span>
            <Badge variant={c.transaction === "vente" ? "vente" : "location"}>
              {t(`v2.immowebSync.tx.${c.transaction === "vente" ? "vente" : "location"}`)}
            </Badge>
            <span className="text-xs text-text-muted">{c.disponibilite}</span>
            <button type="button" onClick={() => onOpenBien(c.id)} className="text-xs text-accent cursor-pointer outline-none">
              {t("v2.immowebSync.bien.open")}
            </button>
            <Button size="sm" className="ml-auto" disabled={busy} onClick={() => onPick(c.id)}>
              {t("v2.immowebSync.associate.thisOne")}
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => onPick(null)}>
          <PlusCircle size={14} /> {t("v2.immowebSync.associate.newBien")}
        </Button>
        {onClose && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
            {t("v2.immowebSync.picker.close")}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Ce qui change (lecture seule) ─────────────────────────────────────────

function ChangesTable({ item }: { item: SyncItem }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto mt-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-text-muted border-b border-border">
            <th className="py-2 pr-4 font-medium">{t("v2.immowebSync.columns.field")}</th>
            <th className="py-2 pr-4 font-medium">{t("v2.immowebSync.columns.before")}</th>
            <th className="py-2 font-medium">{t("v2.immowebSync.columns.after")}</th>
          </tr>
        </thead>
        <tbody>
          {item.diff.map((d) => (
            <tr key={d.field} className="border-b border-border last:border-0 align-top">
              <td className="py-2 pr-4 text-text-primary whitespace-nowrap">{fieldLabel(t, d.field)}</td>
              <td className="py-2 pr-4 text-text-muted">{formatValue(t, d.ours)}</td>
              <td className="py-2 text-text-primary">{formatValue(t, d.theirs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Choix exclusif en boutons (le projet n'utilise jamais de <select> ni de radio natifs). */
function Segmented({
  value,
  options,
  onChange,
  disabled,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex rounded-md border border-border overflow-hidden">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            "px-3 py-1.5 text-xs whitespace-nowrap outline-none transition-colors",
            value === o.value ? "bg-accent text-white" : "bg-surface text-text-secondary hover:bg-gray-100",
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
